import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ImagePlus, Loader2, Trash2, Upload } from 'lucide-react';
import Button from '../../components/ui/Button';
import WorkspaceAvatar from './WorkspaceAvatar';
import { IMAGE_TYPES, validateWorkspaceImage } from './workspaceImage';

/**
 * The branding hero of the workspace settings modal: a click-or-drop logo tile
 * beside the workspace identity it produces.
 *
 * The tile shows the picked file immediately via a local object URL, so the
 * upload reads as instant instead of waiting on PUT → refetch → GET. That
 * preview hands over to the real bytes as soon as `workspace.imageKey` changes,
 * and is dropped if the upload rejects, so the tile never claims an image the
 * server didn't keep.
 *
 * `onFile` must return the upload promise (mutateAsync) for that rollback to work.
 */
export default function WorkspaceLogoField({
  orgId,
  workspace,
  displayName,
  onFile,
  onRemove,
  uploading = false,
  removing = false,
}) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null); // { url, forKey }
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);

  const savedKey = workspace?.imageKey ?? null;
  const hasImage = Boolean(savedKey) || Boolean(preview);
  const busy = uploading || removing;

  // Revoke on replacement and on unmount — one effect owns both ends of the URL.
  useEffect(() => {
    if (!preview) return undefined;
    return () => URL.revokeObjectURL(preview.url);
  }, [preview]);

  // Once the saved key moves, the real (already cache-seeded) bytes take over.
  useEffect(() => {
    setPreview((current) => (current && current.forKey !== savedKey ? null : current));
  }, [savedKey]);

  const submit = async (file) => {
    const problem = validateWorkspaceImage(file);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    const url = URL.createObjectURL(file);
    setPreview({ url, forKey: savedKey });
    try {
      await onFile(file);
    } catch {
      // The modal's onError surfaces the message; here we just stop showing an
      // image that was never saved. Guarded so a newer pick isn't clobbered.
      setPreview((current) => (current?.url === url ? null : current));
    }
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) submit(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (busy) return;
    const file = e.dataTransfer.files?.[0];
    if (file) submit(file);
  };

  return (
    <div>
      <div className="flex items-start gap-4">
        <button
          type="button"
          onClick={() => !busy && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!busy) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          disabled={busy}
          aria-label={hasImage ? 'Replace workspace logo' : 'Upload workspace logo'}
          className={clsx(
            'group relative h-24 w-24 shrink-0 overflow-hidden rounded-xl transition-colors',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600',
            'disabled:cursor-wait',
            dragging
              ? 'border-2 border-brand-600 bg-brand-600/10'
              : hasImage
                ? 'border border-slate-200 bg-slate-50 hover:border-slate-300'
                : 'border-2 border-dashed border-slate-300 bg-slate-50 hover:border-brand-600 hover:bg-brand-600/5',
          )}
        >
          {/* pointer-events-none so dragenter/leave only ever fire on the tile itself */}
          <span className="pointer-events-none block h-full w-full">
            {hasImage ? (
              <WorkspaceAvatar
                orgId={orgId}
                workspace={workspace}
                previewUrl={preview?.url}
                className="h-full w-full rounded-xl"
              />
            ) : (
              <span className="flex h-full w-full flex-col items-center justify-center gap-1 text-slate-400 group-hover:text-brand-600">
                <ImagePlus className="h-6 w-6" />
                <span className="text-[11px] font-medium">Add logo</span>
              </span>
            )}
          </span>

          {/* Hover/focus affordance once there's something to replace. */}
          {hasImage && !busy && (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center gap-1 bg-slate-900/60 text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
              <Upload className="h-3.5 w-3.5" /> Replace
            </span>
          )}
          {busy && (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-900/50">
              <Loader2 className="h-5 w-5 animate-spin text-white" aria-hidden />
            </span>
          )}
        </button>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-800">
            {displayName || workspace?.name}
          </p>
          <p className="text-xs text-slate-500">
            {workspace?.type === 'personal' ? 'Personal workspace' : 'Shared workspace'}
          </p>

          <div className="mt-2.5 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={uploading}
              disabled={removing}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-4 w-4" /> {savedKey ? 'Replace' : 'Upload'}
            </Button>
            {savedKey && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                loading={removing}
                disabled={uploading}
                onClick={onRemove}
              >
                <Trash2 className="h-4 w-4 text-red-500" /> Remove
              </Button>
            )}
          </div>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_TYPES}
        className="hidden"
        onChange={onPick}
      />

      <p className="mt-2.5 text-xs text-slate-400">
        Drop an image on the tile or click to browse · PNG, JPEG, WebP or GIF · up to 2 MB.
      </p>
      {error && (
        <p role="alert" className="mt-1 text-xs font-medium text-red-500">
          {error}
        </p>
      )}
    </div>
  );
}
